import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, IconButton, Typography, Alert } from '@mui/material';
import { Close } from '@mui/icons-material';
import { CaptivePortalFormData } from './types';
import { getTemplateById } from './templateRegistry';
import { CaptivePortal } from '@lib/model/captive-portal-model';

type Props = {
  config: CaptivePortal;
  onClose?: () => void;
  fullScreen?: boolean;
  formData: CaptivePortalFormData;
  onFormDataChange: (data: CaptivePortalFormData) => void;
  onClickSubmit: () => void;
  isSubmitting?: boolean;
};

export const CaptivePortalRenderer: React.FC<Props> = ({
  config,
  onClose,
  fullScreen = false,
  formData,
  onFormDataChange,
  onClickSubmit,
  isSubmitting = false,
}) => {
  const { t } = useTranslation();

  const template = getTemplateById(config.template);

  if (!config.template) {
    const content = (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6" color="textSecondary">
          {t('noTemplateSelected', 'No template selected')}
        </Typography>
      </Box>
    );

    return fullScreen ? (
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 9999,
          backgroundColor: 'white',
        }}
      >
        {onClose && (
          <IconButton
            onClick={onClose}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              zIndex: 10000,
              backgroundColor: 'white',
              '&:hover': {
                backgroundColor: 'white',
              },
            }}
          >
            <Close sx={{ color: 'black' }} />
          </IconButton>
        )}
        {content}
      </Box>
    ) : (
      content
    );
  }

  if (!template) {
    const content = (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Alert severity="error">
          {t('templateNotFound', 'Template not found: {{templateId}}', {
            templateId: config.template,
          })}
        </Alert>
      </Box>
    );

    return fullScreen ? (
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 9999,
          backgroundColor: 'white',
        }}
      >
        {onClose && (
          <IconButton
            onClick={onClose}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              zIndex: 10000,
              backgroundColor: 'white',
              '&:hover': {
                backgroundColor: 'white',
              },
            }}
          >
            <Close sx={{ color: 'black' }} />
          </IconButton>
        )}
        {content}
      </Box>
    ) : (
      content
    );
  }

  const TemplateComponent = template.component;

  const templateContent = (
    <TemplateComponent
      data={config.data ?? {}}
      formData={formData}
      onFormChange={onFormDataChange}
      onSubmit={onClickSubmit}
      isSubmitting={isSubmitting}
    />
  );

  if (fullScreen) {
    return (
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 9999,
          backgroundColor: 'white',
          overflow: 'auto',
        }}
      >
        {onClose && (
          <IconButton
            onClick={onClose}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              zIndex: 10000,
              backgroundColor: 'white',
              '&:hover': {
                backgroundColor: 'white',
              },
            }}
          >
            <Close sx={{ color: 'black' }} />
          </IconButton>
        )}
        {templateContent}
      </Box>
    );
  }

  return templateContent;
};
